import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {OrderFormDraftDto} from '../../models/order-form-draft';

@Injectable({providedIn: 'root'})
export class FormSaverPersistenceService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'direct-booking-order-form-draft';

  /**
   * Restores the locally stored order draft from local storage.
   * Invalid stored data is discarded to avoid reusing corrupted drafts.
   *
   * @returns The restored order draft, or `null` when no usable draft exists.
   */
  public restore(): OrderFormDraftDto | null {
    const storage = this.getLocalStorage();
    if (!storage) {
      return null;
    }

    const rawDraft = storage.getItem(this.storageKey);
    if (!rawDraft) {
      return null;
    }

    try {
      return JSON.parse(rawDraft) as OrderFormDraftDto;
    } catch {
      storage.removeItem(this.storageKey);
      return null;
    }
  }

  /**
   * Stores the current order draft locally so it can be restored on a later visit.
   *
   * @param draft Current order form draft.
   */
  public persistLocal(draft: OrderFormDraftDto): void {
    const storage = this.getLocalStorage();
    if (!storage) {
      return;
    }

    storage.setItem(this.storageKey, JSON.stringify(draft));
  }

  /**
   * Removes the locally stored order draft.
   */
  public clearLocal(): void {
    const storage = this.getLocalStorage();
    if (!storage) {
      return;
    }

    storage.removeItem(this.storageKey);
  }

  /**
   * Safely resolves browser local storage.
   *
   * @returns The browser `localStorage` instance, or `null` outside a browser context.
   */
  private getLocalStorage(): Storage | null {
    return this.document.defaultView?.localStorage ?? null;
  }
}
